import type * as PopoverPrimitive from "@radix-ui/react-popover";
import type { Command as CommandPrimitive } from "cmdk";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults } from "@/ui/lib";

export const COMBOBOX_TRIGGER_ICON: TIconKey = "chevronUpDown";
export const COMBOBOX_ITEM_INDICATOR_ICON: TIconKey = "tick";
export const COMBOBOX_SEARCH_ICON: TIconKey = "search";

export type TComboboxOption = {
  disabled?: boolean;
  icon?: TIconKey;
  label: string;
  value: string;
};

export type TComboboxOptionGroup = {
  label: string;
  options: TComboboxOption[];
};

export type TComboboxOptions = TComboboxOption[] | TComboboxOptionGroup[];
export type TComboboxValue = string | string[];

export type TComboboxRootProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>;
export type TComboboxTriggerElement = ComponentRef<typeof PopoverPrimitive.Trigger>;
export type TComboboxTriggerProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> & {
  error?: string;
  placeholder?: string;
  selectedLabel?: string;
};
export type TComboboxContentElement = ComponentRef<typeof PopoverPrimitive.Content>;
export type TComboboxContentProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  portalContainer?: HTMLElement | null;
};
export type TComboboxCommandElement = ComponentRef<typeof CommandPrimitive>;
export type TComboboxCommandProps = ComponentPropsWithoutRef<typeof CommandPrimitive>;
export type TComboboxInputElement = ComponentRef<typeof CommandPrimitive.Input>;
export type TComboboxInputProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Input>;
export type TComboboxListElement = ComponentRef<typeof CommandPrimitive.List>;
export type TComboboxListProps = ComponentPropsWithoutRef<typeof CommandPrimitive.List>;
export type TComboboxEmptyElement = ComponentRef<typeof CommandPrimitive.Empty>;
export type TComboboxEmptyProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>;
export type TComboboxGroupElement = ComponentRef<typeof CommandPrimitive.Group>;
export type TComboboxGroupProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Group>;
export type TComboboxItemElement = ComponentRef<typeof CommandPrimitive.Item>;
export type TComboboxItemProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
  icon?: TIconKey;
  isSelected?: boolean;
};
export type TComboboxSeparatorElement = ComponentRef<typeof CommandPrimitive.Separator>;
export type TComboboxSeparatorProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>;

export type TComboboxProps = Omit<TComboboxRootProps, "onOpenChange" | "open"> & {
  asChild?: boolean;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  emptyMessage?: string;
  error?: string;
  inputClassName?: string;
  isMultiSelect?: boolean;
  label: string;
  hideLabel?: boolean;
  onValueChange?: (value: TComboboxValue) => void;
  options: TComboboxOptions;
  placeholder?: string;
  required?: boolean;
  searchPlaceholder?: string;
  triggerClassName?: string;
  value?: TComboboxValue;
} & Pick<HTMLAttributes<HTMLElement>, "id">;

type TComboboxDefaultProps = DefinedDefaults<
  TComboboxProps,
  "emptyMessage" |
  "placeholder" |
  "searchPlaceholder"
>;

export const COMBOBOX_DEFAULTS = {
  emptyMessage: "No option found.",
  placeholder: "Select an option",
  searchPlaceholder: "Search...",
} satisfies TComboboxDefaultProps;
